import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect
} from 'react'
import { Project } from '../types/project'
import { Corporate } from '../types/corporate'
import { useSocketManager } from '../hooks/useSocketManager'
import { createCorporate } from '../utils/api/CreateCorporate'
import { updateCorporate } from '../utils/api/UpdateCorporate'
import { getCorporate } from '../utils/api/GetCorporate'
import { getProjectContent } from '../utils/api/GetProjectContent'
import { useCreateRealizeUnit } from '../hooks/useCreateRealizeUnit'
import { useHistory } from 'react-router-dom'
import { getGenerations } from '../utils/api/GetGenerations'
import { updateProject } from '../utils/api/UpdateProject'
import { generateFromDescription } from '../utils/api/GenerateFromDescription'
import { getProject } from '../utils/api/GetProject'
import {
  CreateUnitPrefillSnapshot,
  PrefillError,
  PrefillStatus
} from '../types/createUnitPrefill'
import { simulateMockPrefill } from '../utils/mockCreateUnitPrefill'
import { Folder } from '../types/folder'
import { getFolders as fetchFoldersApi } from '../utils/api/GetFolders'
import { createFolder as createFolderApi } from '../utils/api/CreateFolder'
import { updateFolder as updateFolderApi } from '../utils/api/UpdateFolder'
import { deleteFolder as deleteFolderApi } from '../utils/api/DeleteFolder'
import { toast } from 'react-toastify'

export type CreateUnitTabs =
  | 'basic'
  | 'audience'
  | 'goal'
  | 'competencies'
  | 'indicators'
  | 'material'
  | 'structure'
  | 'generate'

export type FileUploadStatus =
  | 'NONE'
  | 'AWAITING_UPLOAD'
  | 'PROCESSING'
  | 'PROCESSED'
  | 'ERROR'
  | 'DONE'

export type StructureGenerationStatus =
  | 'NONE'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'ENDED'

export type ContentGenerationStatus =
  | 'NONE'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'ENDED'

/** Maps editor unit route id (realizeUnitId) to corporate API project id (CorporateWithProject.project.id). */
export type CorporateEditorBinding = {
  unitId: string
  projectId: number
}

interface ICorporateContext {
  currentCreateUnitTab: CreateUnitTabs
  goToCreateUnitTab: (tab: CreateUnitTabs) => void
  enabledCreateUnitTabs: CreateUnitTabs[]
  enableCreateUnitTab: (tab: CreateUnitTabs) => void
  createUnitData: CreateUnitData
  updateCreateUnitData: (data: CreateUnitData) => void
  updateCorporateData: (
    data: CreateUnitData,
    projectId?: number
  ) => Promise<void>
  generatedDepartmentCompetencies: string[]
  setGeneratedDepartmentCompetencies: (competencies: string[]) => void
  generatedDepartmentIndicators: string[]
  setGeneratedDepartmentIndicators: (indicators: string[]) => void
  project: Project | null
  setProject: (project: Project | null) => void
  corporate: Corporate | null
  setCorporate: (corporate: Corporate | null) => void
  fileUploadStatus: FileUploadStatus
  setFileUploadStatus: (status: FileUploadStatus) => void
  structureGenerationStatus: StructureGenerationStatus
  setStructureGenerationStatus: (status: StructureGenerationStatus) => void
  contentGenerationStatus: ContentGenerationStatus
  setContentGenerationStatus: (status: ContentGenerationStatus) => void
  content: string
  clearFile: () => void
  resetContextWithData: (
    corporate: Corporate,
    project: Project,
    sources: Array<{
      id: number
      frontend_id: string
      filename: string
      contentType: string
      metadata: string
      createdAt: string
    }>
  ) => Promise<void>
  resetContext: () => void
  onCreateRealizeUnit: (
    project: Project,
    content: string,
    corporate: Corporate | null
  ) => Promise<string | null>
  onTabClick: () => Promise<void>
  isCreatingRealizeUnit: boolean
  corporateEditorBinding: CorporateEditorBinding | null
  setCorporateEditorBinding: (binding: CorporateEditorBinding | null) => void
  prefillJobId: string | null
  prefillStatus: PrefillStatus
  prefillCurrentStep: CreateUnitTabs | 'orchestration' | null
  prefillError: PrefillError | null
  startPrefillFromDescription: (description: string) => Promise<void>
  applyPrefillSnapshot: (snapshot: CreateUnitPrefillSnapshot) => void
  resetPrefillState: () => void
  folders: Folder[]
  foldersLoading: boolean
  loadFolders: () => Promise<void>
  createFolder: (name: string) => Promise<Folder | null>
  renameFolder: (id: number, name: string) => Promise<boolean>
  deleteFolder: (id: number) => Promise<boolean>
  moveCorporateToFolder: (
    projectId: number,
    folderId: number | null
  ) => Promise<boolean>
}

const CorporateContext = createContext<ICorporateContext>({
  currentCreateUnitTab: 'basic',
  goToCreateUnitTab: () => undefined,
  enabledCreateUnitTabs: ['basic'],
  enableCreateUnitTab: () => undefined,
  createUnitData: {},
  updateCreateUnitData: () => undefined,
  updateCorporateData: async () => undefined,
  generatedDepartmentCompetencies: [],
  setGeneratedDepartmentCompetencies: () => undefined,
  generatedDepartmentIndicators: [],
  setGeneratedDepartmentIndicators: () => undefined,
  project: null,
  setProject: () => undefined,
  corporate: null,
  setCorporate: () => undefined,
  fileUploadStatus: 'NONE',
  setFileUploadStatus: () => undefined,
  structureGenerationStatus: 'NONE',
  setStructureGenerationStatus: () => undefined,
  contentGenerationStatus: 'NONE',
  setContentGenerationStatus: () => undefined,
  content: '',
  clearFile: () => undefined,
  resetContextWithData: async () => undefined,
  resetContext: () => undefined,
  onCreateRealizeUnit: async () => null,
  onTabClick: async () => undefined,
  isCreatingRealizeUnit: false,
  corporateEditorBinding: null,
  setCorporateEditorBinding: () => undefined,
  prefillJobId: null,
  prefillStatus: 'idle',
  prefillCurrentStep: null,
  prefillError: null,
  startPrefillFromDescription: async () => undefined,
  applyPrefillSnapshot: () => undefined,
  resetPrefillState: () => undefined,
  folders: [],
  foldersLoading: false,
  loadFolders: async () => undefined,
  createFolder: async () => null,
  renameFolder: async () => false,
  deleteFolder: async () => false,
  moveCorporateToFolder: async () => false
})

type ICorporateProvider = {
  children: React.ReactNode
}

type CreateUnitData = {
  title?: string
  workload?: string
  departments?: string[]
  goal?: string
  competencies?: string[]
  expectedBehaviors?: string
  indicators?: string[]
  currentGoal?: string
  materials?: File[]
  structure?: string[]
}

export const useSetupCorporateProvider = () => {
  const { onCreateRealizeUnit } = useCreateRealizeUnit()
  const history = useHistory()

  const [
    currentCreateUnitTab,
    setCurrentCreateUnitTab
  ] = useState<CreateUnitTabs>('basic')
  const [enabledCreateUnitTabs, setEnabledCreateUnitTabs] = useState<
    CreateUnitTabs[]
  >(['basic'])
  const [createUnitData, setCreateUnitData] = useState<CreateUnitData>({})
  const [
    generatedDepartmentCompetencies,
    setGeneratedDepartmentCompetencies
  ] = useState<string[]>([])
  const [
    generatedDepartmentIndicators,
    setGeneratedDepartmentIndicators
  ] = useState<string[]>([])
  const [project, setProject] = useState<Project | null>(null)
  const [corporate, setCorporate] = useState<Corporate | null>(null)
  const [fileUploadStatus, setFileUploadStatus] = useState<FileUploadStatus>(
    'NONE'
  )
  const [
    structureGenerationStatus,
    setStructureGenerationStatus
  ] = useState<StructureGenerationStatus>('PROCESSING')
  const [
    contentGenerationStatus,
    setContentGenerationStatus
  ] = useState<ContentGenerationStatus>('NONE')
  const [content, setContent] = useState<string>('')
  const [isCreatingRealizeUnit, setIsCreatingRealizeUnit] = useState<boolean>(
    false
  )
  const [
    corporateEditorBinding,
    setCorporateEditorBinding
  ] = useState<CorporateEditorBinding | null>(null)
  const [prefillJobId, setPrefillJobId] = useState<string | null>(null)
  const [prefillStatus, setPrefillStatus] = useState<PrefillStatus>('idle')
  const [prefillCurrentStep, setPrefillCurrentStep] = useState<
    CreateUnitTabs | 'orchestration' | null
  >(null)
  const [prefillError, setPrefillError] = useState<PrefillError | null>(null)
  const mockPrefillCleanupRef = useRef<(() => void) | null>(null)
  const [folders, setFolders] = useState<Folder[]>([])
  const [foldersLoading, setFoldersLoading] = useState(false)

  const loadFolders = useCallback(async () => {
    setFoldersLoading(true)
    try {
      const response = await fetchFoldersApi()
      if (response) {
        setFolders(response.data)
      }
    } finally {
      setFoldersLoading(false)
    }
  }, [])

  const createFolder = useCallback(
    async (name: string): Promise<Folder | null> => {
      const folder = await createFolderApi(name)
      if (folder) {
        await loadFolders()
        toast.success('Pasta criada')
        return folder
      }
      toast.error('Não foi possível criar a pasta')
      return null
    },
    [loadFolders]
  )

  const renameFolder = useCallback(
    async (id: number, name: string): Promise<boolean> => {
      const folder = await updateFolderApi(id, name)
      if (folder) {
        await loadFolders()
        toast.success('Pasta renomeada')
        return true
      }
      toast.error('Não foi possível renomear a pasta')
      return false
    },
    [loadFolders]
  )

  const deleteFolder = useCallback(
    async (id: number): Promise<boolean> => {
      const success = await deleteFolderApi(id)
      if (success) {
        await loadFolders()
        const params = new URLSearchParams(history.location.search)
        if (params.get('folder') === String(id)) {
          history.push('/corporate')
        }
        toast.success('Pasta excluída')
        return true
      }
      toast.error('Não foi possível excluir a pasta')
      return false
    },
    [loadFolders, history]
  )

  const moveCorporateToFolder = useCallback(
    async (projectId: number, folderId: number | null): Promise<boolean> => {
      const response = await updateCorporate({
        project_id: projectId,
        folder_id: folderId
      })
      if (response) {
        await loadFolders()
        toast.success('Treinamento movido')
        return true
      }
      toast.error('Não foi possível mover o treinamento')
      return false
    },
    [loadFolders]
  )

  useEffect(() => {
    loadFolders()
  }, [loadFolders])

  const fetchStructure = useCallback(async () => {
    if (project?.id) {
      const response = await getCorporate(project.id)

      if (response?.structure && response.structure.length > 0) {
        setCorporate(response)
        setCreateUnitData((prev) => ({
          ...prev,
          structure: response.structure
        }))
      }
    }
  }, [project])

  const fetchContent = useCallback(async () => {
    setIsCreatingRealizeUnit(true)
    if (project?.id) {
      const response = await getProjectContent(project.id)
      if (response && response.length > 0) {
        const latestContentFound = response.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0]
        const html = latestContentFound?.html || '<p>...</p>'
        const unitId = await onCreateRealizeUnit(project, html, corporate)
        if (unitId) {
          await updateCorporate({
            project_id: project.id,
            realizeUnitId: unitId
          })
        }
        setContent(html)
        if (unitId) history.push(`/corporate/editor/unit/${unitId}`)
      }
    }
    setIsCreatingRealizeUnit(false)
  }, [project, corporate, onCreateRealizeUnit, history])

  const applyPrefillSnapshot = useCallback(
    (snapshot: CreateUnitPrefillSnapshot) => {
      if (snapshot.project) {
        setProject(snapshot.project)
      }

      if (snapshot.corporate) {
        setCorporate(snapshot.corporate)
      }

      if (snapshot.createUnitData) {
        setCreateUnitData((prev) => ({
          ...prev,
          ...snapshot.createUnitData
        }))
      }

      if (snapshot.generatedDepartmentCompetencies) {
        setGeneratedDepartmentCompetencies(
          snapshot.generatedDepartmentCompetencies
        )
      }

      if (snapshot.generatedDepartmentIndicators) {
        setGeneratedDepartmentIndicators(snapshot.generatedDepartmentIndicators)
      }

      if (snapshot.enabledCreateUnitTabs?.length) {
        setEnabledCreateUnitTabs(snapshot.enabledCreateUnitTabs)
      }

      setFileUploadStatus(snapshot.fileUploadStatus)
      setStructureGenerationStatus(snapshot.structureGenerationStatus)
      setContentGenerationStatus(snapshot.contentGenerationStatus)

      if (snapshot.overall_status === 'COMPLETED') {
        setCurrentCreateUnitTab(snapshot.currentCreateUnitTab || 'basic')
      }
    },
    []
  )

  const handleCreateUnitPrefillEvent = useCallback(
    (event: {
      jobId: string
      projectId: number
      status: 'PROCESSING' | 'COMPLETED' | 'ENDED'
      step: CreateUnitTabs | 'orchestration'
      snapshot: CreateUnitPrefillSnapshot
    }) => {
      if (!prefillJobId || event.jobId !== prefillJobId) {
        return
      }

      if (project?.id && event.projectId !== project.id) {
        return
      }

      applyPrefillSnapshot(event.snapshot)
      setPrefillCurrentStep(event.step)

      if (
        event.status === 'ENDED' ||
        event.snapshot.overall_status === 'ENDED'
      ) {
        setPrefillStatus('error')
        setPrefillError(
          event.snapshot.error ?? {
            message: 'Não foi possível gerar o treinamento. Tente novamente.',
            step: event.step !== 'orchestration' ? event.step : undefined
          }
        )
        return
      }

      if (event.snapshot.overall_status === 'COMPLETED') {
        setPrefillStatus('completed')
        setPrefillError(null)
      }
    },
    [prefillJobId, project, applyPrefillSnapshot]
  )

  useSocketManager({
    project,
    prefillJobId,
    setFileUploadStatus,
    setStructureGenerationStatus,
    setContentGenerationStatus,
    fetchStructure,
    fetchContent,
    onCreateUnitPrefillEvent: handleCreateUnitPrefillEvent
  })

  const goToCreateUnitTab = (tab: CreateUnitTabs) => {
    setCurrentCreateUnitTab(tab)
  }

  const enableCreateUnitTab = (tab: CreateUnitTabs) => {
    setEnabledCreateUnitTabs([...enabledCreateUnitTabs, tab])
  }

  const updateCreateUnitData = (data: CreateUnitData) => {
    setCreateUnitData({ ...createUnitData, ...data })
  }

  const updateCorporateData = useCallback(
    async (data: CreateUnitData, projectId?: number) => {
      const updatedData = { ...createUnitData, ...data }

      const effectiveProjectId = projectId || project?.id

      if (!effectiveProjectId) {
        return
      }

      const corporateData = {
        project_id: effectiveProjectId,
        workload: updatedData.workload,
        departments: updatedData.departments,
        goal: updatedData.goal,
        competencies: updatedData.competencies,
        expectedBehaviors: updatedData.expectedBehaviors,
        indicators: updatedData.indicators,
        currentGoal: updatedData.currentGoal
      }

      if (corporate) {
        const response = await updateCorporate(corporateData)
        if (response) {
          setCorporate(response)
        }
      } else {
        const response = await createCorporate(corporateData)
        if (response) {
          setCorporate(response)
        }
      }
    },
    [createUnitData, project, corporate]
  )

  const onTabClick = useCallback(async () => {
    if (project?.id && createUnitData.title) {
      await updateProject(project.id, createUnitData.title)
    }

    await updateCorporateData(createUnitData)
  }, [project, createUnitData, updateCorporateData])

  const clearFile = useCallback(() => {
    setCreateUnitData((prev) => ({ ...prev, materials: undefined }))
    setFileUploadStatus('NONE')
  }, [])

  const resetContextWithData = useCallback(
    async (
      corporateData: Corporate,
      projectData: Project,
      sources: Array<{
        id: number
        frontend_id: string
        filename: string
        contentType: string
        metadata: string
        createdAt: string
      }>
    ) => {
      setCurrentCreateUnitTab('basic')

      const hasSources = sources && sources.length > 0

      const tabsToEnable: CreateUnitTabs[] = ['basic']

      if (projectData.name && corporateData.workload) {
        tabsToEnable.push('audience')
      }

      if (corporateData.departments?.length) {
        tabsToEnable.push('goal')
      }

      if (corporateData.goal) {
        tabsToEnable.push('competencies')
      }

      if (corporateData.competencies?.length) {
        tabsToEnable.push('indicators')
      }

      if (corporateData.indicators?.length) {
        tabsToEnable.push('material')

        if (hasSources || corporateData.structure?.length) {
          tabsToEnable.push('structure')

          if (corporateData.structure?.length) {
            tabsToEnable.push('generate')
          }
        }
      }

      setEnabledCreateUnitTabs(tabsToEnable)

      setCorporate(corporateData)
      setProject(projectData)

      const mockMaterials = hasSources
        ? sources.map((source) => {
            const blob = new Blob([], { type: source.contentType })
            const file = new File([blob], source.filename, {
              type: source.contentType
            })
            Object.defineProperty(file, 'sourceId', {
              value: source.id,
              writable: false
            })
            Object.defineProperty(file, 'frontendId', {
              value: source.frontend_id,
              writable: false
            })
            return file
          })
        : undefined

      setCreateUnitData({
        title: projectData.name,
        workload: corporateData.workload,
        departments: corporateData.departments,
        goal: corporateData.goal || undefined,
        competencies: corporateData.competencies,
        expectedBehaviors: corporateData.expectedBehaviors || undefined,
        indicators: corporateData.indicators,
        currentGoal: corporateData.currentGoal || undefined,
        structure: corporateData.structure,
        materials: mockMaterials
      })

      setFileUploadStatus(hasSources ? 'DONE' : 'NONE')
      setStructureGenerationStatus(
        corporateData.structure?.length ? 'COMPLETED' : 'NONE'
      )
      setContentGenerationStatus('NONE')
      setContent('')

      setGeneratedDepartmentCompetencies([])
      setGeneratedDepartmentIndicators([])
      setCorporateEditorBinding(null)

      const generations = await getGenerations()
      if (generations) {
        const projectGeneration = generations.find(
          (gen) => gen.project_id === projectData.id
        )

        if (projectGeneration) {
          if (projectGeneration.type === 'corporate_structure') {
            setStructureGenerationStatus('PROCESSING')
          } else if (projectGeneration.type === 'contents') {
            setContentGenerationStatus('PROCESSING')
          } else if (projectGeneration.type === 'document') {
            setFileUploadStatus('PROCESSING')
            setEnabledCreateUnitTabs((prevTabs) =>
              prevTabs.filter((tab) => tab !== 'structure')
            )
          }
        }
      }
    },
    []
  )

  const resetContext = useCallback(() => {
    mockPrefillCleanupRef.current?.()
    mockPrefillCleanupRef.current = null
    setPrefillJobId(null)
    setPrefillStatus('idle')
    setPrefillCurrentStep(null)
    setPrefillError(null)
    setCurrentCreateUnitTab('basic')
    setEnabledCreateUnitTabs(['basic'])
    setCorporate(null)
    setProject(null)
    setCreateUnitData({})
    setFileUploadStatus('NONE')
    setStructureGenerationStatus('PROCESSING')
    setContentGenerationStatus('NONE')
    setContent('')
    setGeneratedDepartmentCompetencies([])
    setGeneratedDepartmentIndicators([])
    setCorporateEditorBinding(null)
  }, [])

  const resetPrefillState = useCallback(() => {
    mockPrefillCleanupRef.current?.()
    mockPrefillCleanupRef.current = null
    setPrefillJobId(null)
    setPrefillStatus('idle')
    setPrefillCurrentStep(null)
    setPrefillError(null)
  }, [])

  const startPrefillFromDescription = useCallback(
    async (description: string) => {
      resetContext()
      setPrefillStatus('submitting')
      setPrefillError(null)

      if (process.env.REACT_APP_MOCK_PREFILL === 'true') {
        const mockJobId = `mock-${Date.now()}`
        setPrefillJobId(mockJobId)
        setPrefillStatus('processing')
        setPrefillCurrentStep('orchestration')

        mockPrefillCleanupRef.current = simulateMockPrefill(
          mockJobId,
          description,
          (snapshot) => {
            applyPrefillSnapshot(snapshot)
            setPrefillCurrentStep(snapshot.step)
            if (snapshot.overall_status === 'COMPLETED') {
              setPrefillStatus('completed')
            }
          },
          () => {
            setPrefillStatus('completed')
          }
        )
        return
      }

      const response = await generateFromDescription(description)

      if (!response) {
        setPrefillStatus('error')
        setPrefillError({
          message: 'Não foi possível iniciar a geração. Tente novamente.'
        })
        return
      }

      setPrefillJobId(response.job_id)

      const projectData = await getProject(response.frontend_id)
      if (projectData) {
        setProject(projectData)
      }

      setPrefillStatus('processing')
      setPrefillCurrentStep('orchestration')
    },
    [resetContext, applyPrefillSnapshot]
  )

  return {
    currentCreateUnitTab,
    goToCreateUnitTab,
    enabledCreateUnitTabs,
    enableCreateUnitTab,
    createUnitData,
    updateCreateUnitData,
    updateCorporateData,
    generatedDepartmentCompetencies,
    setGeneratedDepartmentCompetencies,
    generatedDepartmentIndicators,
    setGeneratedDepartmentIndicators,
    project,
    setProject,
    corporate,
    setCorporate,
    fileUploadStatus,
    setFileUploadStatus,
    structureGenerationStatus,
    setStructureGenerationStatus,
    contentGenerationStatus,
    setContentGenerationStatus,
    content,
    clearFile,
    resetContextWithData,
    resetContext,
    onCreateRealizeUnit,
    onTabClick,
    isCreatingRealizeUnit,
    corporateEditorBinding,
    setCorporateEditorBinding,
    prefillJobId,
    prefillStatus,
    prefillCurrentStep,
    prefillError,
    startPrefillFromDescription,
    applyPrefillSnapshot,
    resetPrefillState,
    folders,
    foldersLoading,
    loadFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    moveCorporateToFolder
  }
}

export const CorporateProvider = ({ children }: ICorporateProvider) => {
  const result = useSetupCorporateProvider()

  return (
    <CorporateContext.Provider value={result}>
      {children}
    </CorporateContext.Provider>
  )
}

export const useCorporate = () => useContext(CorporateContext)

export const withCorporateProvider = (
  Component: React.ComponentType<Record<string, unknown>>
) => {
  return (props: Record<string, unknown>) => {
    return (
      <CorporateProvider>
        <Component {...props} />
      </CorporateProvider>
    )
  }
}
